import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Video, Users, X, Mic, MicOff, Camera, CameraOff, Monitor, MessageCircle, Phone, ExternalLink } from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { useAuth } from '../../contexts/AuthContext'
import {
  fetchAllPreferences,
  fetchSessions,
  updateSession,
} from '../../lib/instantData'
import { brandAlert } from '../../contexts/DialogContext'
import GlassCard from '../../components/ui/GlassCard'
interface Client {
  id: string
  name: string
  email: string
  sessionDate: string
  sessionTime: string
  sessionType: string
}

const VideoCall: React.FC = () => {
  const { adminUser } = useAdminAuth()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [selectedClient, setSelectedClient] = useState('')
  const [videoCallLink, setVideoCallLink] = useState('')
  const [copied, setCopied] = useState(false)
  const [roomName, setRoomName] = useState('')

  // Generate room name with random numbers
  const generateRoomName = () => {
    const randomNumbers = Math.floor(Math.random() * 9000) + 1000 // 4-digit number
    const newRoomName = `fitbycassie${randomNumbers}`
    setRoomName(newRoomName)
    return newRoomName
  }

  // Fetch clients with upcoming sessions for dropdown
  const fetchClients = async () => {
    try {
      const now = Date.now()
      const [sessions, preferences] = await Promise.all([
        fetchSessions(),
        fetchAllPreferences(),
      ])

      const upcoming = sessions.filter(
        (s) => s.status === 'scheduled' && Date.parse(s.scheduled_date) >= now
      )

      const prefMap = new Map(preferences.map((p) => [p.user_id, p]))
      const clientsMap = new Map<string, Client>()

      upcoming.forEach((session) => {
        if (clientsMap.has(session.user_id)) return
        const pref = prefMap.get(session.user_id)
        const sessionDate = new Date(session.scheduled_date)
        clientsMap.set(session.user_id, {
          id: session.user_id,
          name: pref?.full_name || session.client_full_name || 'Unknown Client',
          email: pref?.email || session.client_email || 'unknown@email.com',
          sessionDate: sessionDate.toLocaleDateString(),
          sessionTime: sessionDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          sessionType: session.session_type,
        })
      })

      setClients(Array.from(clientsMap.values()))
    } catch (error) {
      console.error('Error fetching clients with upcoming sessions:', error)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const pasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText()
      setVideoCallLink(clipboardText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to paste from clipboard:', err)
    }
  }

  const openMiroTalkRoom = () => {
    const roomName = generateRoomName()
    const mirotalkUrl = `https://sfu.mirotalk.com/?room=${roomName}`
    window.open(mirotalkUrl, '_blank')
  }

  const inviteSelectedClient = async () => {
    if (selectedClient && videoCallLink) {
      const selectedClientData = clients.find(client => client.id === selectedClient)
      if (selectedClientData) {
        try {
          const now = Date.now()
          const sessions = await fetchSessions({ userId: selectedClientData.id })
          const target = sessions
            .filter(
              (s) =>
                s.status === 'scheduled' &&
                s.session_type === selectedClientData.sessionType &&
                Date.parse(s.scheduled_date) >= now
            )
            .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0]

          if (!target) {
            void brandAlert({ title: 'Notice', message: 'No matching upcoming session found for this client.', variant: 'info' })
            return
          }

          await updateSession(target.id, {
            clientJoinUrl: videoCallLink,
            notes: `Invitation sent to ${selectedClientData.name} for video call session.`,
          })

          void brandAlert({ title: 'Done', message: `Invitation sent to ${selectedClientData.name}! They will see a "Join Call" button on their upcoming sessions page.`, variant: 'success' })
        } catch (error) {
          console.error('Error sending invitation:', error)
          void brandAlert({ title: 'Something went wrong', message: 'Failed to send invitation. Please try again.', variant: 'error' })
        }
      }
    } else {
      void brandAlert({ title: 'Something went wrong', message: 'Please select a client and paste the video call link first', variant: 'error' })
    }
  }

  const endSession = async () => {
    if (selectedClient) {
      const selectedClientData = clients.find(client => client.id === selectedClient)
      if (selectedClientData) {
        try {
          const now = Date.now()
          const sessions = await fetchSessions({ userId: selectedClientData.id })
          const target = sessions
            .filter(
              (s) =>
                s.status === 'scheduled' &&
                s.session_type === selectedClientData.sessionType &&
                Date.parse(s.scheduled_date) >= now
            )
            .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0]

          if (!target) {
            void brandAlert({ title: 'Notice', message: 'No matching upcoming session found for this client.', variant: 'info' })
            return
          }

          await updateSession(target.id, {
            clientJoinUrl: '',
            notes: `Video call session completed for ${selectedClientData.name}.`,
            status: 'completed',
            completedAt: Date.now(),
          })

          void brandAlert({ title: 'Done', message: `Session completed for ${selectedClientData.name}. The call button has been removed from their page.`, variant: 'success' })
          
          // Refresh the clients list to remove the completed session
          fetchClients()
          
          // Navigate back to admin dashboard
          navigate('/admin/dashboard')
        } catch (error) {
          console.error('Error ending session:', error)
          void brandAlert({ title: 'Something went wrong', message: 'Failed to end session. Please try again.', variant: 'error' })
        }
      }
    } else {
      void brandAlert({ title: 'Something went wrong', message: 'Please select a client first', variant: 'error' })
    }
  }

  useEffect(() => {
    // Generate room name automatically when component mounts
    generateRoomName()
    fetchClients()
  }, [])

  if (!adminUser) {
    return (
      <div className="panel-page flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-semibold text-ink mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="panel-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back to Dashboard */}
        <div className="mb-6">
          <Link
            to="/admin/dashboard"
            className="flex items-center text-gray-600 hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <GlassCard className="p-6 border-red-200 bg-red-50">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-5 w-5 text-red-400">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
                    {/* Step-by-Step Instructions */}
          <GlassCard className="p-6">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-primary">Create Video Call Room</h3>
              <p className="text-secondary">Follow these steps to set up your video call session</p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-textPrimary mb-3">
                    <strong>Go to the link:</strong>
                  </p>
                  <a 
                    href="https://sfu.mirotalk.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-white border border-accent rounded-lg text-primary hover:bg-highlight transition-all duration-200"
                  >
                    <span className="mr-2">https://sfu.mirotalk.com/</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-textPrimary mb-3">
                    <strong>Enter room name:</strong>
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="px-4 py-2 bg-white border border-accent rounded-lg text-primary font-mono text-sm">
                      {roomName}
                    </div>
                    <button
                      onClick={generateRoomName}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium"
                    >
                      Generate New
                    </button>
                    <button
                      onClick={() => copyToClipboard(roomName)}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium inline-flex items-center"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs font-bold">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-textPrimary">
                    <strong>Join room</strong> and copy the link from your browser URL
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mt-1">
                  <span className="text-white text-xs font-bold">4</span>
                </div>
                <div className="flex-1">
                  <p className="text-textPrimary">
                    <strong>Paste the link below</strong> in the input field
                  </p>
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Client Selection */}
          <GlassCard className="p-6">
            <h3 className="font-display text-lg font-semibold text-ink mb-4">Invite Client with Upcoming Session</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client (with upcoming sessions)
                </label>
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-blue-500"
                >
                  <option value="">Select a client</option>
                  {clients.length > 0 ? (
                    clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name} - {client.sessionDate} at {client.sessionTime} ({client.sessionType})
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No clients with upcoming sessions</option>
                  )}
                </select>
                {clients.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    No clients have upcoming sessions scheduled. Schedule a session first to invite them to a video call.
                  </p>
                )}
              </div>
              
              {/* Video Call Link Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paste Video Call Link
                  </label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={videoCallLink}
                        onChange={(e) => setVideoCallLink(e.target.value)}
                        placeholder="Paste the MiroTalk room link here..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-blue-500"
                      />
                    </div>
                                    <button
                  onClick={pasteFromClipboard}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-gray-800 transition-all duration-200"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? 'Pasted!' : 'Paste'}
                </button>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={inviteSelectedClient}
                  disabled={!selectedClient || !videoCallLink}
                  className="w-full flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-textPrimary transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Users className="h-5 w-5 mr-2" />
                  Invite Selected Client
                </button>
                
                <button
                  onClick={endSession}
                  disabled={!selectedClient}
                  className="w-full flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  End Session
                </button>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  )
}

export default VideoCall 